import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postService, authService } from '../services/api';
import '../styles/Admin.css';

const Admin = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    body: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [telegramWarning, setTelegramWarning] = useState('');

  useEffect(() => {
    // Check if user is admin
    if (!authService.isAdmin()) {
      navigate('/');
      return;
    }
    fetchPosts();
  }, [navigate]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await postService.getAllPostsAdmin();
      setPosts(data);
    } catch (err) {
      setError('Failed to load posts');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setTelegramWarning('');

    try {
      if (editingPost) {
        await postService.updatePost(editingPost._id, formData.title, formData.body);
        setSuccess('Post updated successfully!');
      } else {
        const data = await postService.createPost(formData.title, formData.body);
        setSuccess('Post created successfully!');
        if (data.telegramFailed) {
          setTelegramWarning(`⚠️ Telegram post failed for: "${data.telegramFailed}"`);
        }
      }

      setFormData({ title: '', body: '' });
      setShowForm(false);
      setEditingPost(null);
      fetchPosts();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      body: post.body
    });
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      await postService.deletePost(id);
      setSuccess('Post deleted successfully!');
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete post');
    }
  };

  const handleReupload = async () => {
    setError('');
    setSuccess('');
    setTelegramWarning('');
    try {
      const data = await postService.reuploadPost();
      setSuccess(`Reuploaded: "${data.post.title}"`);
      if (data.telegramFailed) {
        setTelegramWarning(`⚠️ Telegram post failed for: "${data.telegramFailed}"`);
      }
      fetchPosts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Reupload failed');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPost(null);
    setFormData({ title: '', body: '' });
    setError('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button
          onClick={handleReupload}
          className="reupload-button"
        >
          🔁 Reupload Random Post
        </button>
        <button
          onClick={() => setShowForm(!showForm)}
          className="create-button"
        >
          {showForm ? 'Cancel' : '+ Create New Post'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {telegramWarning && <div className="error-message" style={{ backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffc107' }}>{telegramWarning}</div>}

      {showForm && (
        <div className="post-form-card">
          <h2>{editingPost ? 'Edit Post' : 'Create New Post'}</h2>
          <form onSubmit={handleSubmit} className="post-form">
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter post title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="body">Content</label>
              <textarea
                id="body"
                name="body"
                value={formData.body}
                onChange={handleChange}
                required
                rows="15"
                placeholder="Write your post content here..."
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-button">
                {editingPost ? 'Update Post' : 'Create Post'}
              </button>
              <button type="button" onClick={handleCancel} className="cancel-button">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="posts-list">
        <h2>Manage Posts ({posts.length})</h2>
        {posts.length === 0 ? (
          <p className="no-posts">No posts yet. Create your first post!</p>
        ) : (
          <div className="posts-table">
            {posts.map((post) => (
              <div key={post._id} className="post-item">
                <div className="post-item-content">
                  <h3>{post.title}</h3>
                  <p className="post-preview">
                    {post.body.substring(0, 150)}...
                  </p>
                  <div className="post-item-meta">
                    <span>📅 {formatDate(post.createdAt)}</span>
                    <span>👁️ {post.views} views</span>
                  </div>
                </div>
                <div className="post-item-actions">
                  <button
                    onClick={() => handleEdit(post)}
                    className="edit-button"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDelete(post._id)}
                    className="delete-button"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
